import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { CircularProgress } from '@mui/material';
import { bookConsultApi } from '../../../../Service/Cousult.service';
import { getDataFromLocalStorage } from '../../../../Service/getLocalStorage';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { capturePurchasedServiceEvent } from '../../../../Service/serviceData';

const GuestPayment = ({ 
  serviceParams, 
  appointmentData, 
  hostObj, 
  objName, 
  addOn, 
  priceDisplay,
  onBookingComplete 
}: any) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<string>('card');

  const buyConsultHandler = async () => {
    try {
      setLoading(true);
      const localData = getDataFromLocalStorage() || {};

      // Check if user is logged in
      if (!localData.id) {
        setLoading(false);
        toast.error('Please create an account to complete your booking');
        return;
      }

      // Prepare the booking parameters
      let param = {
        ...appointmentData,
        ...serviceParams,
        email: localData.email,
        first_name: localData.first_name,
        last_name: localData.last_name,
        user_id: localData.id,
        phone: localData?.phone,
        paymentMethod: paymentMethod,
        due_date: serviceParams.due_date ? dayjs(serviceParams.due_date).format("YYYY-MM-DD") : undefined,
      };

      let res: any = await bookConsultApi(param);

      if (res?.data?.success) {
        setLoading(false);
        capturePurchasedServiceEvent(serviceParams.service_name);
        onBookingComplete();
      } else {
        let message = res?.data?.message;
        let allMsg = "";
        for (let i = 0; i < message.length; i++) {
          allMsg += message[i];
        }

        toast.error(allMsg);
        setLoading(false);
      }
    } catch (error: any) {
      toast.error(error.message || 'An error occurred during booking');
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return dayjs(dateString).format('dddd, MMMM DD, YYYY');
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return '';
    return dayjs(dateString).format('h:mm A');
  };

  return (
    <div className="step-container step4">
      <Container>
        <Row>
          <div className="col-lg-12">
            <div className="consult-section-heading text-center">
              <h3>Complete Your Booking</h3>
              <p className="my-4">Review your appointment details and proceed to payment.</p>
            </div>
          </div>
        </Row>

        <Row className="justify-content-center">
          <Col lg={8}>
            {/* Appointment Summary */}
            <div className="booking-summary mb-4">
              <h4>Appointment Summary</h4>
              <div className="summary-card p-3 border rounded">
                <Row>
                  <Col md={6}>
                    <div className="summary-item">
                      <strong>Service:</strong> {appointmentData.service_name || 'Virtual Lactation Consultation'}
                    </div>
                    <div className="summary-item">
                      <strong>Date:</strong> {formatDate(appointmentData.appointment_date_time)}
                    </div>
                    <div className="summary-item">
                      <strong>Time:</strong> {formatTime(appointmentData.appointment_date_time)} EST
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="summary-item">
                      <strong>Duration:</strong> 1.5 hours
                    </div>
                    <div className="summary-item">
                      <strong>Type:</strong> Virtual Consultation
                    </div>
                    <div className="summary-item">
                      <strong>Price:</strong> ${appointmentData.total_ammount || priceDisplay.actualPrice || '325'}
                    </div>
                  </Col>
                </Row>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="payment-method mb-4">
              <h4>Payment Method</h4>
              <div className="payment-options">
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="paymentMethod"
                    id="card"
                    value="card"
                    checked={paymentMethod === 'card'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="card">
                    Credit/Debit Card
                  </label>
                </div>
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="paymentMethod"
                    id="paypal"
                    value="paypal"
                    checked={paymentMethod === 'paypal'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="paypal">
                    PayPal
                  </label>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            {paymentMethod === 'card' && (
              <div className="payment-info mb-4">
                <h4>Payment Information</h4>
                <div className="payment-form p-3 border rounded">
                  <Row>
                    <Col md={12} className="mb-3">
                      <label className="form-label">Card Number</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="1234 5678 9012 3456"
                        maxLength={19}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6} className="mb-3">
                      <label className="form-label">Expiry Date</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="MM/YY"
                        maxLength={5}
                      />
                    </Col>
                    <Col md={6} className="mb-3">
                      <label className="form-label">CVV</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="123"
                        maxLength={4}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12} className="mb-3">
                      <label className="form-label">Cardholder Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="John Doe"
                      />
                    </Col>
                  </Row>
                </div>
              </div>
            )}

            {/* Total and Book Button */}
            <div className="booking-total text-center">
              <div className="total-amount mb-3">
                <h3>Total: ${appointmentData.total_ammount || priceDisplay.actualPrice || '325'}</h3>
              </div>
              <button
                className="btn primary-blue-btn btn-lg"
                onClick={buyConsultHandler}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} className="me-2" />
                    Processing...
                  </>
                ) : (
                  'Complete Booking'
                )}
              </button>
            </div>

            {/* Security Notice */}
            <div className="security-notice mt-4 text-center">
              <small className="text-muted">
                <i className="fas fa-lock me-1"></i>
                Your payment information is secure and encrypted.
              </small>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default GuestPayment;
